import { useCallback, useMemo, useState } from 'react';
import { ClassCard } from '@/components/molecules';
import { ClassModal } from '@/components/organisms';
import { dwrClasses, dwrClassesById } from '@/data/classes';
import type { ClassId } from '@/schemas/classId';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

// Tagline epica per ogni classe (3-8 parole, no verbo principale, tono lirico).
// Approvate con Francesco in review PR 4. Ispirate agli intro DWR delle classi.
const CLASS_TAGLINES: Record<ClassId, string> = {
  barbaro: 'Fame di Furia, Gloria e di Vita',
  bardo: 'La Parola incide la realtà',
  chierico: 'La fede grida dove gli dèi tacciono',
  druido: 'Ascolta il respiro della terra',
  guerriero: 'Acciaio temprato dalla battaglia',
  ladro: 'Il mondo è una cassa da svuotare',
  mago: "La conoscenza è un'arma",
  monaco: 'Ora, labora, pugna',
  paladino: 'Mano armata di una volontà superiore',
  ranger: 'Il sentiero nascosto, la preda braccata',
  sciamano: "Dialogo con l'Aldilà, sempre a un costo",
  warlock: 'Il Patto: un prezzo per il Potere',
};

/**
 * Step 1 — Selettore classe.
 *
 * Re-design Fase 2 PR 4: griglia 2-col responsive di <ClassCard> + modal di
 * preview <ClassModal>. Click su una card NON seleziona direttamente — apre
 * il modal con dettaglio intro, stat-chips e CTA "Scegli {nome}".
 *
 * Cambiare classe azzera archetipo, mosse, equipaggiamento, aspetti e
 * incantesimi: il wipe è gestito da `applyClassChange` nel reducer (vedi
 * `reducer.ts`), non da questo componente.
 */
export function SectionClass() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();
  const [previewId, setPreviewId] = useState<ClassId | null>(null);

  const previewClassDef = previewId ? (dwrClassesById[previewId] ?? null) : null;
  const isCurrent = previewId !== null && draft.classId === previewId;

  // **2026-05-25** — Per il carosello del modal serve passare anche le
  // classi adiacenti (prev/next) con wrap-around alfabetico. Permette al
  // modal di renderizzare 3 card affiancate (prev | current | next) e
  // animare lo swipe come una vera "carrellata orizzontale".
  const adjacentClasses = useMemo(() => {
    if (previewId === null) return { prev: undefined, next: undefined };
    const idx = dwrClasses.findIndex((c) => c.id === previewId);
    if (idx === -1) return { prev: undefined, next: undefined };
    const len = dwrClasses.length;
    return {
      prev: dwrClasses[(idx - 1 + len) % len],
      next: dwrClasses[(idx + 1) % len],
    };
  }, [previewId]);

  function handleChoose() {
    if (!previewId) return;
    dispatch({ type: 'SET_CLASS', classId: previewId });
    setPreviewId(null);
  }

  // **2026-05-25** — Swipe mobile sul `<ClassModal>` cicla tra le classi
  // alfabetiche (`dwrClasses`) con wrap-around. La logica vive qui perché
  // il modal è agnostico al catalog; il caller decide come navigare.
  const cycleClass = useCallback((direction: 'prev' | 'next') => {
    setPreviewId((current) => {
      if (current === null) return current;
      const index = dwrClasses.findIndex((c) => c.id === current);
      if (index === -1) return current;
      const delta = direction === 'next' ? 1 : -1;
      const nextIndex = (index + delta + dwrClasses.length) % dwrClasses.length;
      return dwrClasses[nextIndex]?.id ?? current;
    });
  }, []);

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
        {dwrClasses.map((classDef) => (
          <ClassCard
            key={classDef.id}
            classId={classDef.id}
            name={classDef.name}
            tagline={CLASS_TAGLINES[classDef.id]}
            selected={draft.classId === classDef.id}
            onClick={() => setPreviewId(classDef.id)}
          />
        ))}
      </div>
      <ClassModal
        classDef={previewClassDef}
        prevClassDef={adjacentClasses.prev}
        nextClassDef={adjacentClasses.next}
        isCurrent={isCurrent}
        onClose={() => setPreviewId(null)}
        onChoose={handleChoose}
        onPrev={() => cycleClass('prev')}
        onNext={() => cycleClass('next')}
      />
    </>
  );
}
